import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { fieldErrorsOf } from '@/api/client';
import { setPlatformBitriseToken, setPlatformExpoToken, usePutPlatformProvider } from '@/api/endpointsPlatformProviders';
import type { PlatformProvider } from '@/api/schemasPlatformProviders';
import { savedToast, validateTokenForm, type PlatformProviderKind, type TokenField } from './platformProvidersModel';

/** Sends the token once; the API checks it with the provider, stores it and answers the provider without it. */
function save(kind: PlatformProviderKind, token: string, workspace: string): Promise<PlatformProvider> {
  return kind === 'expo' ? setPlatformExpoToken({ token: token.trim() }) : setPlatformBitriseToken({ token: token.trim(), workspace: workspace.trim() });
}

/** The provider's refusal on a field (422), or null when the problem is not about one. */
function fieldProblems(err: unknown): Partial<Record<TokenField, string>> | null {
  const fields = fieldErrorsOf(err);
  const mine = { ...(fields.token ? { token: fields.token } : {}), ...(fields.workspace ? { workspace: fields.workspace } : {}) };
  return Object.keys(mine).length ? mine : null;
}

/**
 * State and submit of the Set / Replace token sheet. The token lives only in this form: it is sent once (a plain call,
 * not a cached mutation), cleared as soon as it is saved, and the page shows only the account it belongs to.
 */
export function usePlatformTokenForm(current: PlatformProvider, onDone: () => void) {
  const kind = current.provider;
  const put = usePutPlatformProvider();
  const [token, setToken] = useState('');
  const [workspace, setWorkspace] = useState(current.workspace ?? '');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [server, setServer] = useState<Partial<Record<TokenField, string>>>({});
  const [problem, setProblem] = useState<unknown>(null);
  const local = validateTokenForm(kind, { token, workspace });
  const errors = submitted ? { ...server, ...local } : server;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setProblem(null);
    setServer({});
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      const saved = await save(kind, token, workspace);
      setToken('');
      put(saved);
      const t = savedToast(saved);
      toast.success(t.title, { description: t.description });
      onDone();
    } catch (err) {
      const fields = fieldProblems(err);
      if (fields) setServer(fields);
      else setProblem(err);
    } finally {
      setBusy(false);
    }
  };
  const edit = (field: TokenField, value: string) => {
    setServer((s) => ({ ...s, [field]: undefined }));
    if (field === 'token') setToken(value);
    else setWorkspace(value);
  };
  return { kind, token, workspace, errors, problem, busy, edit, onSubmit };
}

export type PlatformTokenForm = ReturnType<typeof usePlatformTokenForm>;
